/**
 * Public FDR vendor portal: an invited outside entity proves its link, enters
 * the code sent to it, uploads the universe, reads the validation and submits
 * it as final to the plan. No Core session, no app shell.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ResultsView from './ResultsView';
import { Notice, Pill, btn, input, label as labelCls } from './ui';
import { API } from './api';

const STEP = { VERIFYING: 'verifying', NEEDS_MFA: 'needs_mfa', AWAITING_OTP: 'awaiting_otp', READY: 'ready', UPLOADING: 'uploading', REVIEWING: 'reviewing', FINALIZED: 'finalized', ERROR: 'error' };

function Shell({ wide, children }) {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="bg-white border-b border-slate-200">
        <div className={`${wide ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-6 py-4 flex items-center justify-between`}>
          <div>
            <div className="text-sm font-bold text-slate-800 tracking-tight">Universe submission portal</div>
            <div className="text-xs text-slate-500">Secure link for an invited vendor</div>
          </div>
          <Pill tone="emerald">Secure</Pill>
        </div>
      </header>
      <main className={`${wide ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-6 py-8`}>{children}</main>
    </div>
  );
}

export default function ScrubberPortal() {
  const { token } = useParams();
  const [step, setStep] = useState(STEP.VERIFYING);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [mfaPass, setMfaPass] = useState(null);
  const [file, setFile] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [review, setReview] = useState(null);
  const [finalized, setFinalized] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };
  const call = async (path, opts) => {
    const r = await fetch(`${API}${path}`, opts);
    const json = await r.json().catch(() => ({}));
    if (!r.ok || json.success === false) { const e = new Error(json.message || `Request failed (${r.status})`); e.code = json.code; throw e; }
    return json;
  };

  useEffect(() => {
    let dead = false;
    call('/fdr/portal/verify', { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth }, body: '{}' })
      .then((j) => { if (!dead) { setInvitation(j.invitation); setStep(STEP.NEEDS_MFA); } })
      .catch((e) => { if (!dead) { setError(e.message || 'This invitation is not valid.'); setStep(STEP.ERROR); } });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function requestCode() {
    setBusy(true); setError('');
    try { const j = await call('/fdr/portal/mfa/challenge', { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth }, body: '{}' }); setChallenge(j.challenge); setStep(STEP.AWAITING_OTP); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function verifyCode() {
    if (!/^\d{6}$/.test(otp)) { setError('Enter the 6 digit code.'); return; }
    setBusy(true); setError('');
    try { const j = await call('/fdr/portal/mfa/verify', { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth }, body: JSON.stringify({ token, challenge_id: challenge?.challenge_id, code: otp }) }); setMfaPass(j.mfa.pass_token); setStep(STEP.READY); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function upload() {
    if (!file) { setError('Choose the file first.'); return; }
    setStep(STEP.UPLOADING); setError('');
    try {
      const fd = new FormData(); fd.append('file', file, file.name); fd.append('token', token); fd.append('mfa_pass', mfaPass);
      const j = await call('/fdr/portal/submit', { method: 'POST', headers: { ...auth, 'X-MFA-Pass': mfaPass }, body: fd });
      setSubmission(j.submission); setReview({ universe: j.universe || [], errors: j.errors || [], categoryCounts: j.categoryCounts || {}, totalRows: j.totalRows ?? (j.universe || []).length, timelinessSummary: j.timelinessSummary || null }); setStep(STEP.REVIEWING);
    } catch (e) { setError(e.code === 'MAX_ATTEMPTS_EXCEEDED' ? (e.message || 'No more attempts are available for this invitation.') : e.message); setStep(STEP.READY); }
  }
  async function finalize() {
    const n = (review?.errors || []).length;
    if (!window.confirm(n ? `Your file still has ${n} validation issue${n === 1 ? '' : 's'}. The plan will see them with your submission. Submit as final anyway?` : 'Your file is clean. Submit as final to the plan?')) return;
    setBusy(true); setError('');
    try { const j = await call('/fdr/portal/finalize', { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth, 'X-MFA-Pass': mfaPass } }); setFinalized(j); setMfaPass(null); setStep(STEP.FINALIZED); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const inv = invitation || {};
  const box = 'bg-white rounded-2xl border border-slate-200 shadow-md p-6';

  if (step === STEP.VERIFYING) return <Shell><div className={box}><div className="text-sm text-slate-500">Opening your secure link…</div></div></Shell>;
  if (step === STEP.ERROR) return <Shell><div className={box}><h2 className="text-lg font-semibold text-slate-900">This link cannot be used</h2><p className="text-sm text-slate-700 mt-2">{error}</p><p className="text-xs text-slate-500 mt-3">Ask the compliance team that invited you for a fresh link.</p></div></Shell>;

  const context = (
    <section className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">For {inv.fdr_organization_name}</p>
      <h1 className="text-lg font-bold text-slate-800 tracking-tight">{inv.protocol} universe <span className="font-normal text-slate-500">{inv.universe_type}</span></h1>
      <p className="text-sm text-slate-500 mt-0.5">Period {inv.universe_period_start} to {inv.universe_period_end}{inv.expires_at ? ` · link expires ${new Date(inv.expires_at).toLocaleDateString()}` : ''}</p>
      {inv.message_to_fdr && <div className="mt-3"><Notice tone="sky">{inv.message_to_fdr}</Notice></div>}
    </section>
  );

  if (step === STEP.REVIEWING && review) {
    return (
      <Shell wide>
        {context}
        {error && <div className="mb-3"><Notice tone="red">{error}</Notice></div>}
        <ResultsView result={review} title="Your validation results" meta={`${file?.name || ''}${submission?.attempt ? ` · attempt ${submission.attempt}` : ''}`}
          actions={<><button type="button" className={btn.secondarySmall} onClick={() => { setFile(null); setReview(null); setSubmission(null); setStep(STEP.READY); }} disabled={busy}>Upload a corrected file</button><button type="button" className={btn.primary} onClick={finalize} disabled={busy}>{busy ? 'Submitting…' : 'Submit as final to the plan'}</button></>} />
      </Shell>
    );
  }

  return (
    <Shell>
      {context}
      <div className={box}>
        {error && <div className="mb-3"><Notice tone="red">{error}</Notice></div>}
        {step === STEP.NEEDS_MFA && (
          <>
            <h2 className="text-base font-semibold text-slate-900">Prove it is you</h2>
            <p className="text-sm text-slate-600 mt-1">A one time code will be sent {inv.mfa_channel === 'sms' ? `by text message to ${inv.fdr_phone_masked || 'your mobile'}` : `by email to ${inv.fdr_email_masked || 'your address'}`}.</p>
            <button type="button" className={`${btn.primary} mt-4`} onClick={requestCode} disabled={busy}>{busy ? 'Sending…' : 'Send the code'}</button>
          </>
        )}
        {step === STEP.AWAITING_OTP && (
          <>
            <h2 className="text-base font-semibold text-slate-900">Enter the code</h2>
            <p className="text-sm text-slate-600 mt-1">{challenge?.sent_to ? `Sent to ${challenge.sent_to}.` : 'Check your messages.'}</p>
            <div className="mt-4 max-w-xs"><label className={labelCls}>6 digit code</label><input className={`${input} text-lg tracking-widest`} inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} /></div>
            <div className="flex items-center gap-3 mt-4"><button type="button" className={btn.primary} onClick={verifyCode} disabled={busy}>{busy ? 'Checking…' : 'Continue'}</button><button type="button" className={btn.link} onClick={requestCode} disabled={busy}>Send a new code</button></div>
          </>
        )}
        {(step === STEP.READY || step === STEP.UPLOADING) && (
          <>
            <h2 className="text-base font-semibold text-slate-900">Upload the universe file</h2>
            <p className="text-sm text-slate-600 mt-1">The file is validated against the CMS record layout for {inv.universe_type}. You will see every issue before you submit as final.</p>
            <label className="block mt-4 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/70 hover:bg-slate-50 hover:border-orange-200 px-6 py-8 text-center cursor-pointer transition">
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.txt" onChange={(e) => setFile(e.target.files && e.target.files[0])} disabled={step === STEP.UPLOADING} />
              <div className="text-sm font-semibold text-slate-800">{file ? file.name : 'Choose the file'}</div>
              <div className="text-xs text-slate-500 mt-1">Excel workbook or CSV</div>
            </label>
            <button type="button" className={`${btn.primary} mt-4`} onClick={upload} disabled={!file || step === STEP.UPLOADING}>{step === STEP.UPLOADING ? 'Validating…' : 'Validate'}</button>
          </>
        )}
        {step === STEP.FINALIZED && (
          <>
            <Pill tone="emerald">Submitted</Pill>
            <h2 className="text-base font-semibold text-slate-900 mt-2">Your universe was submitted as final to the plan.</h2>
            <p className="text-sm text-slate-600 mt-2">{finalized?.message || 'The compliance team now has your file and its validation. You can close this page.'}</p>
            {finalized?.submission?.id && <p className="text-xs text-slate-500 mt-2">Receipt {finalized.submission.id}</p>}
          </>
        )}
      </div>
    </Shell>
  );
}
